import { useTourFiltersTotalCount } from '../../composables/tour/TourUseFiltersTotalCount';
import { getTourInitialFiltersState, setTourInitialFilterState } from '../../model/tour/TourInitial.ts';
import type { TTourFiltersPageState, TTourFiltersState } from '../../types/tour/state';
import type { LocationQuery, Router } from 'vue-router';
import { tourBuildQueryFilterParams } from '@/entities/filter/store/utils/tour/TourBuildQueryFilterParams.ts';
import { tourCheckAppliedFilters } from '@/entities/filter/store/utils/tour/TourCheckAppliedFilters.ts';
import { getPathWithoutTag } from '@/entities/filter/store/utils/removeTagFromPath.ts';
import type { ExtendedPartTagType } from '@/entities/rubrics';
import { applyRubricSelectionState } from '@/entities/filter/store/utils/applyRubricSelection.ts';

export const useTourFiltersStore = defineStore('tour-filters-state', () => {
  const router = ref<Router | null>(null);

  function initRouter(r: Router) {
    router.value = r;
  }
  /**
   * Состояние фильтров от последнего полного поиска
   */
  const searchState: TTourFiltersState = reactive(getTourInitialFiltersState());
  /**
   * Состояние текущих выбранных фильтров (до прожатия кнопки "показать")
   */
  const state: TTourFiltersState = reactive(getTourInitialFiltersState());

  /**
   * Параметры фильтров, устанавливаемые на уровне страницы
   */
  const pageParams: TTourFiltersPageState = reactive({});

  /**
   * TODO переделать! Пока что флаг следит за тем, что первые Э уже прогрузились
   */
  const isFirstExperiencesLoaded = ref(false);

  /**
   * Получение сведений о количестве предложений при выборе фильтров (до применения)
   */
  const { totalCount, isLoading } = useTourFiltersTotalCount(state, pageParams);

  /**
   * Получение сведений о количестве предложений после применения фильтров
   */
  const { totalCount: searchTotalCount, isLoading: isSearchLoading } = useTourFiltersTotalCount(
    searchState,
    pageParams,
  );

  /**
   * Выполнение поиска по текущим установленным фильтрам
   */
  function search() {
    const stateClone = structuredClone(toRaw(state));
    Object.assign(searchState, stateClone);
    setFilterQueryParams();
  }

  function setFilterQueryParams() {
    if (!router.value) {
      return;
    }

    let currentPath = searchState.selected_rubric?.url
      ? searchState.selected_rubric.url
      : router.value?.currentRoute?.path;

    if (!state.tag__slugs) {
      currentPath = getPathWithoutTag(
        router.value?.currentRoute?.path || '',
        (router.value?.currentRoute?.params?.tag as string) || '',
      );
    }

    const searchParams = new URLSearchParams(window.location.search);
    const currentQuery = Object.fromEntries(searchParams.entries());

    const params = tourBuildQueryFilterParams(searchState);

    const nextQuery = {
      ...currentQuery,
      ...params,
    };

    for (const [k, v] of Object.entries(params)) {
      if (v === undefined) {
        delete nextQuery[k];
      }
    }

    router.value?.push({
      path: currentPath || '',
      query: nextQuery,
    });
  }

  function resetModalFilters() {
    const keysToReset = [
      'tourFormat',
      'price',
      'duration',
      'comfort_type',
      'accommodation_type',
      'activity_level_category',
      'childrenAllowed',
      'sorting',
      'persons',
    ] satisfies (keyof TTourFiltersState)[];
    keysToReset.forEach((key) => {
      reset(key);
    });
  }

  /**
   * Сброс всех изменений фильтров до состояния последнего поиска
   */
  function reset(key: keyof TTourFiltersState | 'modal') {
    const initial = getTourInitialFiltersState();

    if (key === 'modal') {
      resetModalFilters();

      return;
    }

    Object.assign(state, { [key]: initial[key] });
  }

  function setInitialStore(queryParams: LocationQuery, tag: string) {
    // Сохраняем selected_rubric перед обновлением, чтобы не потерять его при парсинге query параметров
    const savedSelectedRubric = state.selected_rubric || searchState.selected_rubric;

    const defaultParams = setTourInitialFilterState(queryParams, tag);
    Object.assign(state, defaultParams);

    // Восстанавливаем selected_rubric, если он был установлен и tag совпадает
    if (savedSelectedRubric && (state.tag__slugs === savedSelectedRubric.value || tag === savedSelectedRubric.value)) {
      state.selected_rubric = savedSelectedRubric;
    }

    Object.assign(searchState, structuredClone(toRaw(state)));
  }

  function applyPreviousParams() {
    Object.assign(state, structuredClone(toRaw(searchState)));
  }

  function applyRubricSelection(rubric: ExtendedPartTagType) {
    applyRubricSelectionState(state, rubric);
    search();
  }

  const isFiltersApplied = computed(() => tourCheckAppliedFilters(searchState));

  return {
    state,
    searchState,
    isLoading,
    totalCount,
    pageParams,
    isFirstExperiencesLoaded,
    setInitialStore,
    search,
    applyRubricSelection,
    reset,
    applyPreviousParams,
    initRouter,
    isFiltersApplied,
    searchTotalCount,
    isSearchLoading,
  };
});
